import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type AgentData = {
  tools: ("transferConversation" | "discardLead" | "scheduleVisit" | "assignAgent")[];
  systemPrompt: string;
};

type StepAgentProps = {
  onSubmit: (data: AgentData) => Promise<void>;
  initialData: AgentData | null;
};

const availableTools = [
  { value: "transferConversation", label: "Transferir al prospecto" },
  { value: "discardLead", label: "Descartar prospecto" },
  { value: "scheduleVisit", label: "Agendar citas" },
  { value: "assignAgent", label: "Asignar agente" },
];

const defaultTools: ("transferConversation" | "discardLead" | "scheduleVisit" | "assignAgent")[] = [
  "transferConversation",
  "discardLead",
  "scheduleVisit",
];

export const StepAgent = ({ onSubmit, initialData }: StepAgentProps) => {
  const [formData, setFormData] = useState<AgentData>({
    tools: defaultTools,
    systemPrompt: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleToolsChange = (checked: boolean, tool: string) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        tools: [...prev.tools, tool as any],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        tools: prev.tools.filter((t) => t !== tool),
      }));
    }
  };

  const handleSystemPromptChange = (systemPrompt: string) => {
    setFormData((prev) => ({
      ...prev,
      systemPrompt,
    }));
  };

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const isFormValid = () => {
    return formData.tools.length > 0 && formData.systemPrompt.trim() !== "";
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-lg font-semibold mb-4">Paso 3: Configurar agente</p>
      </div>

      <div>
        <Label className="font-semibold mb-4 block">Herramientas disponibles</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Selecciona las herramientas que el agente podrá utilizar. Por defecto se incluyen:
          transferir conversación, descartar lead y programar visita.
        </p>

        <div className="grid grid-cols-2 gap-4">
          {availableTools.map((tool) => (
            <div key={tool.value} className="flex items-center gap-2">
              <Checkbox
                id={tool.value}
                checked={formData.tools.includes(tool.value as any)}
                onCheckedChange={(checked) => handleToolsChange(checked as boolean, tool.value)}
              />
              <Label htmlFor={tool.value} className="font-normal cursor-pointer">
                {tool.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="systemPrompt" className="font-semibold">
          System Prompt
        </Label>
        <p className="text-sm text-muted-foreground mb-4">
          Escribe las instrucciones que definirán el comportamiento del agente. Este texto será
          usado como el prompt del sistema para el agente de IA.
        </p>
        <Textarea
          id="systemPrompt"
          value={formData.systemPrompt}
          onChange={(e) => handleSystemPromptChange(e.target.value)}
          placeholder="Eres un agente de bienes raíces especializado en ayudar a los clientes a encontrar propiedades. Tu objetivo es..."
          className="min-h-[200px] resize-y font-mono text-sm"
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!isFormValid()}>
          Continuar
        </Button>
      </div>
    </div>
  );
};
